// @flow
import React from 'react';
import styled from 'styled-components';
import { usePagination, useSortBy, useTable, useTableState } from 'react-table';
import { isBrowser } from 'react-device-detect';
import Button from 'components/common/Button';
import { fromNow } from 'lib/common';
import cx from 'classnames';
import './EstimateBoxTable.scss';

const Styles = styled.div`
  padding-top: 1rem;

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    font-size: 100%;
    font: inherit;
    overflow-x: scroll;

    a {
      color: #339af0;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }

    th {
      font-weight: bold;
      border: 1px solid #ccc;
      padding: 4px 5px;
      white-space: nowrap;
    }

    td {
      border: 1px solid #ccc;
      padding: 9px 2px;
      text-align: center;

      @media (min-width: 2560px) {
        .uploadBtn {
          padding: 0.5rem 12px;
        }
      }
    }

    td:nth-child(1),
    td:nth-child(6) {
      width: 72px;
      min-width: 72px;
      font-size: 14px;
    }
    td:nth-child(2) {
      min-width: 144px;
    }
    td:nth-child(3),
    td:nth-child(7) {
      min-width: 200px;
    }
    td:nth-child(4) {
      width: 56px;
      min-width: 56px;
    }
    td:nth-child(5),
    td:nth-child(8) {
      width: 120px;
      min-width: 120px;
      font-size: 14px;
    }
    td:nth-child(9) {
      width: 72px;
      min-width: 72px;
    }

    tr {
      border-top: 1px solid #ccc;
      background-color: #fff;
    }

    tr:nth-child(2n) {
      background-color: #f8f8f8;
    }

    tr.isDisabled td:nth-child(3) a {
      color: currentColor;
      opacity: 0.5;
      text-decoration: none;
      pointer-events: none;
    }
  }

  .pagination {
    padding: 0.5rem;
  }
`;

const defaultColumn = {
  sort: 'numeric',
};

// Let's add a fetchData method to our Table component that will be used to fetch
// new data when pagination state changes
// We can also add a loading state to let our table know it's loading new data
function Table({
  columns,
  data,
  fetchData,
  loading,
  pageCount: controlledPageCount,
  onUploadEstimate,
  onRemoveEstimate,
  onViewEstimate,
  serverData,
  setData,
  totalCount,
}) {
  // Use useTableState to hoist the state (and state updater) out of the table and control it
  const tableState = useTableState({ pageIndex: 0 });

  // Now we can get our table state from the hoisted table state tuple
  const [{ pageIndex, pageSize }] = tableState;

  // Listen for changes in pagination and use the state to fetch our new data
  React.useEffect(() => {
    fetchData({ pageIndex, pageSize });
  }, [fetchData, pageIndex, pageSize]);

  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      debug: false,
      state: tableState, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      // hook that we'll handle our own data fetching
      // This means we'll also have to provide our own
      // pageCount.
      pageCount: controlledPageCount,
    },
    useSortBy,
    usePagination,
  );

  // Render the UI for your table
  return (
    <div className="Table">
      <div className="show-menu">
        <div className="total-count">{`총 ${totalCount}건`}</div>
        <select
          className="select-option"
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[10, 20, 30, 40, 50].map(newPageSize => (
            <option key={newPageSize} value={newPageSize}>
              {newPageSize}건씩 보기
            </option>
          ))}
        </select>
      </div>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>{column.sorted ? (column.sortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {page.map((row, i) => {
            const requestType = row.original.request_type;
            const isDisabled = !!requestType;

            return (
              prepareRow(row) || (
                <tr {...row.getRowProps()} className={cx({ isDisabled })}>
                  {row.cells.map(cell => {
                    const date1 = cell.value ? cell.value.split('/') : null;

                    switch (cell.column.id) {
                      case 'request_id':
                        return <td {...cell.getCellProps()}>{cell.value.split('-')[0]}</td>;
                      case 'request_email':
                        return <td {...cell.getCellProps()}>{cell.value}</td>;
                      case 'request_path':
                        return (
                          <td {...cell.getCellProps()}>
                            <a href={`https://images.cacheby.com/${cell.value}`} target="_blank">
                              {date1[date1.length - 1]}
                            </a>
                          </td>
                        );
                      case 'request_type':
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.value === 'complete' ? '종료' : '요청중'}
                          </td>
                        );
                      case 'request_created_at':
                        return <td {...cell.getCellProps()}>{fromNow(cell.value)}</td>;
                      case 'response_id':
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.value ? cell.value.split('-')[0] : null}
                          </td>
                        );
                      case 'response_path':
                        return (
                          <td {...cell.getCellProps()}>
                            <a href={`https://images.cacheby.com/${cell.value}`} target="_blank">
                              {date1 ? date1[date1.length - 1] : null}
                            </a>
                          </td>
                        );
                      case 'response_created_at':
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.value ? fromNow(cell.value) : null}
                          </td>
                        );
                      case 'response_action':
                        return (
                          <td {...cell.getCellProps()}>
                            {!row.values.response_id
                              ? row.values.request_type !== 'complete' && (
                                <Button
                                  theme="default"
                                  onClick={() => onUploadEstimate(row.values.request_id)}
                                  desktop={isBrowser}
                                  className="uploadBtn"
                                >
                                  업로드
                                </Button>
                              )
                              : row.values.request_type !== 'complete' && (
                                <Button
                                  theme="default"
                                  color="red"
                                  onClick={() => onRemoveEstimate(row.values.response_id, i)}
                                  desktop={isBrowser}
                                >
                                  삭제
                                </Button>
                                )}
                          </td>
                        );
                      default:
                        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                    }
                  })}
                </tr>
              )
            );
          })}
          {loading ? (
            // Use our custom loading state to show a loading indicator
            <tr>
              <td>Loading...</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {/*
        Pagination can be built however you'd like.
        This is just a very basic UI implementation:
      */}
      <div className="pagination">
        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} desktop={isBrowser}>
          {'맨앞'}
        </Button>
        <Button onClick={() => previousPage()} disabled={!canPreviousPage} desktop={isBrowser}>
          {'<'}
        </Button>
        <span className="page">
          <strong>
            {pageIndex + 1} / {pageOptions.length}
          </strong>
        </span>
        <Button onClick={() => nextPage()} disabled={!canNextPage} desktop={isBrowser}>
          {'>'}
        </Button>
        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} desktop={isBrowser}>
          {'맨뒤'}
        </Button>
      </div>
    </div>
  );
}

type Props = {
  estimate: any,
  onUploadEstimate: any,
  onRemoveEstimate: any,
  onViewEstimate: any,
};

const EstimateBoxTable = ({
  estimate,
  onUploadEstimate,
  onRemoveEstimate,
  onViewEstimate,
}: Props) => {
  const serverData = estimate.data;
  const columns = React.useMemo(
    () => [
      {
        Header: '견적요청',
        columns: [
          {
            Header: '요청 ID',
            accessor: 'request_id',
          },
          {
            Header: '이메일',
            accessor: 'request_email',
          },
          {
            Header: '파일명',
            accessor: 'request_path',
          },
          {
            Header: '상태',
            accessor: 'request_type',
          },
          {
            Header: '요청 날짜',
            accessor: 'request_created_at',
          },
        ],
      },
      {
        Header: '파트너',
        columns: [
          {
            Header: '응답 ID',
            accessor: 'response_id',
          },
          {
            Header: '견적 응답 파일',
            accessor: 'response_path',
          },
          {
            Header: '응답 날짜',
            accessor: 'response_created_at',
          },
          {
            Header: '액션',
            accessor: 'response_action',
          },
        ],
      },
    ],
    [],
  );

  // We'll start our table without any data
  const [data, setData] = React.useState(serverData);
  const [loading, setLoading] = React.useState(false);
  const [pageCount, setPageCount] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(serverData.length);

  const fetchData = React.useCallback(
    ({ pageSize, pageIndex }) => {
      // This will get called when the table needs new data
      // You could fetch your data from literally anywhere,
      // even a server. But for this example, we'll just fake it.

      // Set the loading state
      setLoading(true);

      // We'll even set a delay to simulate a server here
      setTimeout(() => {
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;
        setData(serverData.slice(startRow, endRow));
        setTotalCount(serverData.length);

        // Your server could send back total page count.
        // For now we'll just fake it, too
        setPageCount(Math.ceil(serverData.length / pageSize));

        setLoading(false);
      }, 1000);
    },
    [serverData],
  );

  return (
    <div className="EstimateBoxTable">
      <Styles>
        <Table
          className="Table"
          onUploadEstimate={onUploadEstimate}
          onRemoveEstimate={onRemoveEstimate}
          onViewEstimate={onViewEstimate}
          columns={columns}
          data={data}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          serverData={serverData}
          setData={setData}
          totalCount={totalCount}
        />
      </Styles>
    </div>
  );
};

export default EstimateBoxTable;



// WEBPACK FOOTER //
// ./src/components/estimate/EstimateBoxTable/EstimateBoxTable.js